import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import axios from 'axios'

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024
export const MAX_ATTACHMENTS_PER_MESSAGE = 10

export type AttachmentDraftStatus = 'uploading' | 'ready' | 'error'

/**
 * Estado de um arquivo selecionado pelo usuário durante a redação de uma
 * mensagem. `ready` significa que o `PUT` ao GCS terminou e o `gcsPath` está
 * disponível para enviar ao backend.
 */
export interface AttachmentDraft {
  /** Identificador local — usado pela UI para reconciliação. */
  localId: string
  file: File
  status: AttachmentDraftStatus
  progress: number
  /** Preenchido quando `status === 'ready'`. */
  gcsPath: string | null
  errorMessage?: string
}

export interface PendingAttachmentLike {
  gcsPath: string
  name: string
  size: number
  type: string
}

export interface PreparedAttachmentUpload {
  uploadUrl: string
  gcsPath: string
  name: string
  type: string
  size: number
  /** Headers que DEVEM ser ecoados no PUT — fazem parte da assinatura. */
  uploadHeaders?: Record<string, string>
}

/** Converte `AttachmentDraft` em payload aceito pelos endpoints de create. */
export function draftsToPendingAttachments(
  drafts: AttachmentDraft[],
): PendingAttachmentLike[] {
  return drafts
    .filter((d) => d.status === 'ready' && d.gcsPath)
    .map((d) => ({
      gcsPath: d.gcsPath as string,
      name: d.file.name,
      size: d.file.size,
      type: d.file.type || 'application/octet-stream',
    }))
}

export interface UseAttachmentUploadOptions {
  /** Quando false, `addFiles` avisa e não faz nada (ex.: recurso-alvo ainda não definido). */
  enabled: boolean
  /** Chama o endpoint de "prepare upload" específico do domínio (processo, issue, etc.). */
  prepareUploads: (
    files: Array<{ originalName: string; contentType: string; size: number }>,
  ) => Promise<PreparedAttachmentUpload[]>
  /** Mensagem de toast quando `enabled` é false e o usuário tenta anexar. */
  disabledMessage?: string
  /**
   * Validação client-side extra por arquivo (ex.: restringir a imagens),
   * além do tamanho/contagem já embutidos aqui. Retorna `true` se o arquivo
   * pode seguir, ou uma string com a mensagem de erro pra mostrar via toast.
   * Só evita uma viagem desnecessária ao servidor — o backend faz a mesma
   * checagem de verdade (nunca confiar só na validação client-side).
   */
  isFileAllowed?: (file: File) => true | string
}

/**
 * Gerencia uploads de anexos genérico — usado tanto por Ocorrências
 * (`features/occurrences/hooks/useAttachmentUpload.ts`, que injeta
 * `prepareUploads` apontando pro endpoint de processo) quanto por Problemas
 * Reportados (que injeta o endpoint de issue). Fluxo:
 *  1. `addFiles(files)` — adiciona drafts em `uploading` e dispara prepare +
 *     PUT direto ao GCS em paralelo. Atualiza progresso e status.
 *  2. `drafts` é o array exposto pra UI renderizar chips/preview.
 *  3. `removeDraft(localId)` — remove um draft (não cancela uploads em voo,
 *     simplesmente esquece o resultado).
 *  4. `reset()` — limpa todos os drafts (após envio bem-sucedido).
 *
 * Rejeita arquivos > 25 MB e impede ultrapassar 10 anexos somados.
 */
export function useAttachmentUpload({
  enabled,
  prepareUploads,
  disabledMessage = 'Não é possível anexar arquivos agora.',
  isFileAllowed,
}: UseAttachmentUploadOptions) {
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([])

  const uploadOne = useCallback(
    async (localId: string, file: File): Promise<void> => {
      if (!enabled) return
      try {
        const [prepared] = await prepareUploads([
          {
            originalName: file.name,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
          },
        ])
        if (!prepared) {
          throw new Error('Backend não retornou signed URL')
        }
        await axios.put(prepared.uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            ...prepared.uploadHeaders,
          },
          onUploadProgress: (e) => {
            if (!e.total) return
            const pct = Math.round((e.loaded / e.total) * 100)
            setDrafts((prev) =>
              prev.map((d) =>
                d.localId === localId ? { ...d, progress: pct } : d,
              ),
            )
          },
        })
        setDrafts((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? {
                  ...d,
                  status: 'ready',
                  progress: 100,
                  gcsPath: prepared.gcsPath,
                }
              : d,
          ),
        )
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Falha ao enviar'
        setDrafts((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? { ...d, status: 'error', errorMessage: msg }
              : d,
          ),
        )
        toast.error(`${file.name}: ${msg}`)
      }
    },
    [enabled, prepareUploads],
  )

  const addFiles = useCallback(
    (files: File[]) => {
      if (!enabled) {
        toast.error(disabledMessage)
        return
      }
      const accepted: File[] = []
      let currentCount = drafts.length
      for (const f of files) {
        if (currentCount >= MAX_ATTACHMENTS_PER_MESSAGE) {
          toast.error(
            `Máximo de ${MAX_ATTACHMENTS_PER_MESSAGE} anexos por mensagem.`,
          )
          break
        }
        if (f.size > MAX_ATTACHMENT_BYTES) {
          toast.error(
            `${f.name}: excede o limite de ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB.`,
          )
          continue
        }
        const allowed = isFileAllowed?.(f) ?? true
        if (allowed !== true) {
          toast.error(allowed)
          continue
        }
        accepted.push(f)
        currentCount++
      }
      if (accepted.length === 0) return

      const newDrafts: AttachmentDraft[] = accepted.map((f) => ({
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        status: 'uploading',
        progress: 0,
        gcsPath: null,
      }))
      setDrafts((prev) => [...prev, ...newDrafts])
      for (const d of newDrafts) {
        void uploadOne(d.localId, d.file)
      }
    },
    [drafts.length, enabled, disabledMessage, isFileAllowed, uploadOne],
  )

  const removeDraft = useCallback((localId: string) => {
    setDrafts((prev) => prev.filter((d) => d.localId !== localId))
  }, [])

  const reset = useCallback(() => {
    setDrafts([])
  }, [])

  const isUploading = drafts.some((d) => d.status === 'uploading')
  const hasErrors = drafts.some((d) => d.status === 'error')

  return {
    drafts,
    addFiles,
    removeDraft,
    reset,
    isUploading,
    hasErrors,
  }
}
